import type { IQuestion } from '@repo/types/questions/Question.interface';
import type {
  AnswerUserQuestionFill,
  ExportAnswerFill,
  FeedbackAnswerQuestionFill,
  FeedbackAnswerQuestionFillItem,
  GapQuestionFill,
  QuestionFillContent,
  QuestionFillData,
  QuestionFillSerialized,
} from '@repo/types/questions/QuestionFill';
import type { CorrectType } from '@repo/types/quiz';

import { QuestionType } from '@repo/types/enums/questionType';

export class QuestionFill implements IQuestion {
  readonly type: QuestionType;
  readonly content: string;
  private readonly points: number = 0;
  private gapsContent: string;
  private gaps: GapQuestionFill[];
  private selectedAnswer: AnswerUserQuestionFill | null = null;
  private marked: FeedbackAnswerQuestionFill | null = null;
  constructor(data: QuestionFillData, selectedAnswer: AnswerUserQuestionFill | null, marked: FeedbackAnswerQuestionFill | null) {
    this.type = QuestionType.FILL;
    this.content = data.content;
    this.points = data.points;
    this.gapsContent = data.gapsContent;
    this.gaps = data.gaps;
    this.selectedAnswer = selectedAnswer;
    this.marked = marked;
  }

  getContent(): QuestionFillContent {
    return {
      content: this.content,
      gaps: this.gaps,
      gapsContent: this.gapsContent,
    };
  }

  getMaxPoints(): number {
    return this.points;
  }

  getPoints(): number {
    let points: number = 0;

    if (this.marked == null) return 0;

    Object.values(this.marked).forEach((gap) => {
      points += gap?.points ?? 0;
    });

    return points;
  }

  setAnswer(answers: Record<string, string | null>): void {
    this.selectedAnswer = answers;
  }

  getAnswer(): Record<string, string | null> | null {
    return this.selectedAnswer;
  }

  isAnswered(): boolean {
    return this.selectedAnswer ? Object.values(this.selectedAnswer).filter((v) => v != null).length > 0 : false;
  }

  resetAnswer(): void {
    this.selectedAnswer = null;
  }

  setMarked(marked: FeedbackAnswerQuestionFill): void {
    this.marked = marked;
  }

  getMarked(): FeedbackAnswerQuestionFill | null {
    return this.marked;
  }

  isMarked(): boolean {
    return !(this.marked == null);
  }

  correctState(): CorrectType {
    if (!this.isAnswered()) return null;

    if (this.marked && this.marked.answers) {
      const userAnswers: FeedbackAnswerQuestionFillItem[] = Object.values(this.marked.answers);
      const total: number = this.gaps.length;
      const correctCount: number = userAnswers.filter((a) => a.correct).length;
      if (correctCount === total) return true;
      if (correctCount > 0) return 'partially';
      return false;
    }

    const points: number = this.getPoints();
    const maxPoints: number = this.getMaxPoints();

    if (points == 0) return false;
    return points == maxPoints ? true : 'partially';
  }

  toJSON(): QuestionFillSerialized {
    return {
      marked: this.marked,
      selectedAnswer: this.selectedAnswer,
    };
  }

  importAnswer(answer: ExportAnswerFill): void {
    if (Object.keys(answer).length > 0) {
      const completeAnswers = { ...answer };
      this.gaps.forEach((gap) => {
        if (!(gap.id in completeAnswers)) {
          completeAnswers[gap.id] = null;
        }
      });
      this.selectedAnswer = completeAnswers;
    } else {
      this.selectedAnswer = null;
    }
  }

  exportAnswer(): ExportAnswerFill {
    const exportAnswer: ExportAnswerFill = {};
    this.gaps.forEach((gap) => {
      exportAnswer[gap.id] = this.selectedAnswer?.[gap.id] ?? null;
    });
    return exportAnswer;
  }
}
